import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Button, Card, CardBody, Col, Progress, Row } from "reactstrap";
import { WorkOrderStatus } from "features/WorkOrders/constants/work-order.constants";
import {
  createWorkOrderTask,
  deleteWorkOrderTask,
  updateWorkOrderTask,
} from "features/WorkOrders/services/WorkOrderService";
import type { WorkOrderTask } from "features/WorkOrders/types/work-order.types";
import { useWorkOrderOverview } from "./WorkOrderOverviewContext";
import TasksSummaryFilters, {
  type TaskSummaryFilter,
} from "../WorkOrderTasks/TasksSummaryFilters";
import WorkOrderTaskItem from "../WorkOrderTasks/WorkOrderTaskItem";
import WorkOrderTaskFormModal from "../WorkOrderTasks/WorkOrderTaskFormModal";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";

export default function TasksTab() {
  const { can } = useAccess();
  const canMutateTasksPerm = can(Permissions.WORK_ORDER.UPDATE);
  const { workOrder, refetch } = useWorkOrderOverview();
  const tasks = useMemo(() => workOrder?.tasks ?? [], [workOrder?.tasks]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<TaskSummaryFilter>("all");
  const [busyTaskId, setBusyTaskId] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formTaskId, setFormTaskId] = useState<number | null>(null);
  const [formTitle, setFormTitle] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formBusy, setFormBusy] = useState(false);

  const canMutate = useMemo(() => {
    const status = workOrder?.status as WorkOrderStatus | undefined;
    return (
      !!workOrder &&
      canMutateTasksPerm &&
      status !== WorkOrderStatus.CANCELLED
    );
  }, [workOrder, canMutateTasksPerm]);

  const totalTasks = tasks.length;
  const completedCount = tasks.filter((t) => t.isCompleted).length;
  const pendingCount = totalTasks - completedCount;

  const filteredTasks = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tasks.filter((task) => {
      if (filter === "pending" && task.isCompleted) return false;
      if (filter === "completed" && !task.isCompleted) return false;
      if (!q) return true;
      const descPlain = (task.description ?? "")
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
      return (
        task.title?.toLowerCase().includes(q) ||
        descPlain.includes(q)
      );
    });
  }, [tasks, filter, query]);

  const openAddModal = () => {
    setFormTaskId(null);
    setFormTitle("");
    setFormDescription("");
    setFormOpen(true);
  };

  const openEditModal = (task: WorkOrderTask) => {
    if (!task.id) return;
    setFormTaskId(task.id);
    setFormTitle(task.title ?? "");
    setFormDescription(task.description ?? "");
    setFormOpen(true);
  };

  const closeFormModal = () => {
    if (formBusy) return;
    setFormOpen(false);
  };

  const handleSubmitForm = async () => {
    if (!workOrder?.id || !formTitle.trim()) return;
    setFormBusy(true);
    try {
      const descTrim = formDescription.trim();
      const payload = {
        title: formTitle.trim(),
        description:
          descTrim.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim()
            ? descTrim
            : null,
      };
      const res =
        formTaskId == null
          ? await createWorkOrderTask(workOrder.id, payload)
          : await updateWorkOrderTask(workOrder.id, formTaskId, payload);
      if (res.success) {
        toast.success(
          res.message || (formTaskId == null ? "Task added" : "Task updated"),
        );
        setFormOpen(false);
        setFormTaskId(null);
        setFormTitle("");
        setFormDescription("");
        await refetch();
      } else {
        toast.error(
          res.message ||
            (formTaskId == null
              ? "Could not add task"
              : "Could not update task"),
        );
      }
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ??
          (formTaskId == null ? "Could not add task" : "Could not update task"),
      );
    } finally {
      setFormBusy(false);
    }
  };

  const handleToggleCompleted = async (task: WorkOrderTask) => {
    if (!workOrder?.id || !task.id) return;
    setBusyTaskId(task.id);
    try {
      const res = await updateWorkOrderTask(workOrder.id, task.id, {
        isCompleted: !task.isCompleted,
      });
      if (res.success) {
        toast.success(res.message || "Task updated");
        await refetch();
      } else {
        toast.error(res.message || "Could not update task");
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? "Could not update task");
    } finally {
      setBusyTaskId(null);
    }
  };

  const handleDelete = async (task: WorkOrderTask) => {
    if (!workOrder?.id || !task.id) return;
    setBusyTaskId(task.id);
    try {
      const res = await deleteWorkOrderTask(workOrder.id, task.id);
      if (res.success) {
        toast.success(res.message || "Task deleted");
        await refetch();
      } else {
        toast.error(res.message || "Could not delete task");
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? "Could not delete task");
    } finally {
      setBusyTaskId(null);
    }
  };

  return (
    <Row>
      <Col>
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between mb-3">
              <h6 className="text-uppercase">Tasks</h6>
              <Button
                color="primary"
                size="sm"
                disabled={!canMutate}
                onClick={openAddModal}
              >
                <i className="ri-add-line align-bottom me-1" />
                Add task
              </Button>
            </div>
            <TasksSummaryFilters
              totalTasks={totalTasks}
              completedCount={completedCount}
              pendingCount={pendingCount}
              filter={filter}
              setFilter={setFilter}
              query={query}
              setQuery={setQuery}
            />
            {totalTasks > 0 ? (
              <div className="d-flex align-items-center gap-2 mb-3">
                <div className="flex-grow-1">
                  <Progress
                    value={totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0}
                    color={completedCount === totalTasks ? "success" : "primary"}
                    style={{ height: "6px" }}
                  />
                </div>
                <span className="text-muted small text-nowrap">
                  {completedCount} / {totalTasks} done
                </span>
              </div>
            ) : null}
            {!canMutate ? (
              <p className="text-muted small mb-3">
                Tasks are locked for closed/cancelled work orders.
              </p>
            ) : null}
            {tasks.length === 0 ? (
              <p className="text-muted mb-0">
                No tasks added for this work order.
              </p>
            ) : (
              <div className="vstack gap-3">
                {filteredTasks.length === 0 ? (
                  <p className="text-muted mb-0">
                    No tasks match your current filter.
                  </p>
                ) : null}
                {filteredTasks.map((task, index) => (
                  <WorkOrderTaskItem
                    key={task.id ?? `${task.title}-${index}`}
                    task={task}
                    index={index}
                    canMutate={canMutate}
                    busy={busyTaskId === task.id}
                    onEdit={openEditModal}
                    onToggleCompleted={handleToggleCompleted}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
            <WorkOrderTaskFormModal
              open={formOpen}
              isEdit={formTaskId != null}
              busy={formBusy}
              title={formTitle}
              description={formDescription}
              canSubmit={!!formTitle.trim()}
              onClose={closeFormModal}
              onChangeTitle={setFormTitle}
              onChangeDescription={setFormDescription}
              onSubmit={handleSubmitForm}
            />
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
}
